import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import "./ImageCropModal.css";

// Percentage-based crop area (0–100), image-resolution-independent
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MediaSize {
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
}

interface Props {
  imageUrl: string;
  aspect: number;
  type: "cover" | "avatar";
  /** Previously saved crop area in percentages — restores position on re-open */
  initialCroppedArea?: CropArea | null;
  onSave: (area: CropArea) => void;
  onClose: () => void;
}

/**
 * Full-screen modal wrapping react-easy-crop.
 *
 * Key design decisions:
 *
 * 1. minZoom is always 1 (passed to the Cropper).
 *    Previously minZoom was set to safeMin (fill-zoom), which locked the user into a
 *    zoomed-in view for portrait/tall images — they couldn't zoom out to see the full
 *    image. Now minZoom=1 so users can always scroll/slide left to see the complete
 *    image and choose which part to crop.
 *    `safeMin` is still computed and used for the initial zoom (starts filled) and
 *    for maxZoom (safeMin * 4).
 *
 * 2. cropperReady waits for naturalSize in ALL cases.
 *    Prevents onCropComplete from firing at zoom=1 before fill-zoom is computed,
 *    which previously produced out-of-bounds crop areas (width > 100%).
 *
 * 3. handleSave clamps the crop area to [0..100] before persisting.
 *    When the user is zoomed out below fill-zoom, react-easy-crop can return negative
 *    x/y or width/height > 100. Clamping ensures getCropStyle always gets valid input.
 */
const ImageCropModal: React.FC<Props> = ({
  imageUrl,
  aspect,
  type,
  initialCroppedArea,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  // safeMin = fill-zoom (zoom needed to cover the crop box with no black bars).
  // Used only for initial zoom and maxZoom — NOT passed as minZoom to the Cropper.
  const [safeMin, setSafeMin] = useState(1);
  // Pre-populate with the previously saved area so "Bitti" saves correctly
  // even when the user opens the modal and clicks Bitti without moving.
  const [croppedArea, setCroppedArea] = useState<CropArea | null>(initialCroppedArea ?? null);

  // Natural image dimensions — needed for zoom pre-computation and
  // for converting saved % crop → pixels for initialCroppedAreaPixels.
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Computes safeMin (fill-zoom) and the correct initial zoom.
   * Returns null if the container is not yet measurable.
   */
  const computeZooms = useCallback(
    (naturalW: number, naturalH: number): { safeMin: number; initialZoom: number } | null => {
      const containerEl = containerRef.current;
      if (!containerEl || containerEl.clientWidth === 0 || containerEl.clientHeight === 0) {
        return null;
      }

      const containerW = containerEl.clientWidth;
      const containerH = containerEl.clientHeight;
      const containerAspect = containerW / containerH;

      // Crop rectangle dimensions inside the container
      let cropW: number;
      let cropH: number;
      if (containerAspect >= aspect) {
        cropH = containerH;
        cropW = cropH * aspect;
      } else {
        cropW = containerW;
        cropH = cropW / aspect;
      }

      // Image display size at zoom=1 (react-easy-crop fits image via object-fit:contain)
      const imgNatAspect = naturalW / naturalH;
      let imgDisplayW: number;
      let imgDisplayH: number;
      if (imgNatAspect >= containerAspect) {
        imgDisplayW = containerW;
        imgDisplayH = containerW / imgNatAspect;
      } else {
        imgDisplayH = containerH;
        imgDisplayW = containerH * imgNatAspect;
      }

      // safeMin = smallest zoom that covers the crop box in both axes (no black bars)
      const zoomX = cropW / imgDisplayW;
      const zoomY = cropH / imgDisplayH;
      const needed = Math.ceil(Math.max(zoomX, zoomY) * 100) / 100;
      const computedSafeMin = Math.max(1, needed);

      let initialZoom: number;
      if (!initialCroppedArea) {
        // No saved crop — start at fill-zoom (image covers crop box on open)
        initialZoom = computedSafeMin;
      } else {
        // Derive zoom from saved crop-width percentage:
        // At zoom z, displayed image width = imgDisplayW * z.
        // The crop occupies (width/100) of the image, so in display space:
        //   (width/100) * imgDisplayW * z = cropW  →  z = cropW / ((width/100) * imgDisplayW)
        const zFromCrop = cropW / ((initialCroppedArea.width / 100) * imgDisplayW);
        // Clamp within [1, safeMin*4] so the slider can reach it
        initialZoom = Math.max(1, Math.min(zFromCrop, computedSafeMin * 4));
      }

      return { safeMin: computedSafeMin, initialZoom };
    },
    [aspect, initialCroppedArea]
  );

  // Phase 1: preload image to get natural dimensions.
  // safeMin and zoom are pre-computed BEFORE the Cropper mounts so the Cropper's
  // very first onCropComplete always fires with a valid crop area.
  // naturalSize is set LAST — this flips cropperReady to true and mounts the Cropper.
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const zooms = computeZooms(w, h);
      if (zooms) {
        setSafeMin(zooms.safeMin);
        setZoom(zooms.initialZoom);
      }
      setNaturalSize({ w, h });
    };
    img.onerror = () => setNaturalSize({ w: 0, h: 0 }); // fallback: unblock the Cropper
    img.src = imageUrl;
  }, [imageUrl, computeZooms]);

  // Phase 2: convert stored percentage area → pixel area for react-easy-crop.
  const initialCroppedAreaPixels = useMemo<CropArea | undefined>(() => {
    if (!initialCroppedArea || !naturalSize || naturalSize.w === 0) return undefined;
    return {
      x:      (initialCroppedArea.x      / 100) * naturalSize.w,
      y:      (initialCroppedArea.y      / 100) * naturalSize.h,
      width:  (initialCroppedArea.width  / 100) * naturalSize.w,
      height: (initialCroppedArea.height / 100) * naturalSize.h,
    };
  }, [initialCroppedArea, naturalSize]);

  /**
   * Called by react-easy-crop after its internal image load.
   * Re-runs computeZooms as a safety net for container resize between Phase 1 and mount.
   */
  const handleMediaLoaded = useCallback(
    (mediaSize: MediaSize) => {
      const zooms = computeZooms(mediaSize.naturalWidth, mediaSize.naturalHeight);
      if (zooms) {
        setSafeMin(zooms.safeMin);
        setZoom(zooms.initialZoom);
      }
    },
    [computeZooms]
  );

  const onCropComplete = useCallback(
    (area: CropArea /* 0-100 percentages */, _pixels: CropArea) => {
      setCroppedArea(area);
    },
    []
  );

  const handleSave = () => {
    if (!croppedArea) { onClose(); return; }

    // Clamp to valid image bounds [0..100].
    // Needed when user saves while zoomed out below fill-zoom: react-easy-crop can
    // return negative coordinates or dimensions > 100 in that state.
    const x      = Math.max(0, croppedArea.x);
    const y      = Math.max(0, croppedArea.y);
    const right  = Math.min(100, croppedArea.x + croppedArea.width);
    const bottom = Math.min(100, croppedArea.y + croppedArea.height);
    const w = right - x;
    const h = bottom - y;

    if (w > 0 && h > 0) {
      onSave({ x, y, width: w, height: h });
    } else {
      onClose();
    }
  };

  // Wait for naturalSize in ALL cases — ensures the Cropper always mounts with the
  // correct zoom set, so the first onCropComplete is always valid.
  const cropperReady = naturalSize !== null;

  return (
    <div className="icm-overlay" onClick={onClose}>
      <div
        className={`icm-modal icm-modal--${type}`}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="icm-hint">{t("crop_drag_hint")}</p>

        <div className="icm-cropper-container" ref={containerRef}>
          {cropperReady ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              // Always 1 — users can slide left to zoom out and see the full image.
              // safeMin (fill-zoom) is used for initialZoom and maxZoom only.
              minZoom={1}
              maxZoom={safeMin * 4}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={handleMediaLoaded}
              // Restores the exact previously-saved crop area (pixel-based)
              initialCroppedAreaPixels={initialCroppedAreaPixels}
              showGrid={true}
              restrictPosition={true}
            />
          ) : (
            // Brief loading state while the image preloads for dimension calculation
            <div className="icm-loading">…</div>
          )}
        </div>

        <div className="icm-footer">
          <div className="icm-zoom-row">
            <span className="icm-zoom-label">{t("crop_zoom")}</span>
            <input
              type="range"
              className="icm-zoom-slider"
              min={1}
              max={safeMin * 4}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </div>
          <div className="icm-actions">
            <button type="button" className="icm-btn icm-btn--cancel" onClick={onClose}>
              {t("admin_cancel")}
            </button>
            <button type="button" className="icm-btn icm-btn--save" onClick={handleSave}>
              {t("profile_cover_reposition_done")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;